import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent} from 'react';

import {BLUE} from 'ui-library/Colors';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import {ElementMouseEvent} from 'util/Types';

export interface CheckableSortableTableRow {
  id: string | number;
  checked: boolean;
  disabled?: boolean;
}

/** @package */
export interface CheckableSortableTableProps {
  columns: Array<SortableTableColumn>;
  rows: Array<CheckableSortableTableRow>;
  onChangeChecks?: (newCheckedRowIds: Array<string | number>) => void;
  disableCheckboxes: boolean;
  className?: string;
  onClickRow?: (row: CheckableSortableTableRow, e?: ElementMouseEvent) => void;
  emptyTableText?: string;
  clickable?: boolean;
  selectable?: boolean;
  hoverable?: boolean;
  coloredHeader?: boolean;
  verticalAlignMiddle?: boolean;
  wrapCell?: boolean;
}

/**
 * (BNCH-13149) CheckableSortableTable is outdated, but we don't have a suitable replacement for it so cannot
 * mark it as legacy yet. We should either write a replacement or update the implementation and behavior.
 */
class CheckableSortableTable extends React.Component<CheckableSortableTableProps> {
  static defaultProps = {disableCheckboxes: false};

  private getCheckedRowIds = (): Array<string | number> =>
    this.props.rows.filter(({checked}) => checked).map(({id}) => id);

  private handleCheckRow = (rowId: string | number, e: ChangeEvent<HTMLInputElement>): void => {
    const newCheckedRowIds: Array<string | number> = e.target.checked
      ? [...this.getCheckedRowIds(), rowId]
      : this.getCheckedRowIds().filter((rid) => rid !== rowId);

    if (typeof this.props.onChangeChecks === 'function') {
      this.props.onChangeChecks(newCheckedRowIds);
    }
  };

  private getRowsWithCheckboxes = (): Array<{checked: JSX.Element; id: string | number}> =>
    this.props.rows.map((row) => {
      const checkbox = (
        <input
          type="checkbox"
          className={css(styles.checkbox)}
          checked={row.checked}
          disabled={this.props.onChangeChecks == null || this.props.disableCheckboxes || row.disabled}
          onChange={(e) => {
            this.handleCheckRow(row.id, e);
          }}
        />
      );
      return {
        ...row,
        checked: checkbox,
      };
    });

  private handleAllCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    let newCheckedRowIds: Array<string | number>;
    const disabledCheckedRowIds = this.props.rows
      .filter(({disabled, checked}) => disabled && checked)
      .map(({id}) => id);
    const disabledUncheckedRowIds = this.props.rows
      .filter(({disabled, checked}) => disabled && !checked)
      .map(({id}) => id);
    if (e.target.checked) {
      newCheckedRowIds = this.props.rows
        .map(({id}) => id)
        // don't check currently unchecked disabled rows
        .filter((id) => !disabledUncheckedRowIds.includes(id));
    } else {
      // keep disabled checked rows checked
      newCheckedRowIds = disabledCheckedRowIds;
    }
    if (typeof this.props.onChangeChecks === 'function') {
      this.props.onChangeChecks(newCheckedRowIds);
    }
  };

  render(): JSX.Element {
    const allCheckbox = (
      <IndeterminateCheckbox
        items={this.props.rows.map(({checked}) => checked)}
        selectedMap={(checked) => checked}
        disabled={this.props.onChangeChecks == null || this.props.disableCheckboxes}
        onChange={this.handleAllCheckboxChange}
      />
    );
    const checkboxColumn = SortableTable.defaultColumn(allCheckbox, 'checked', {sortable: false});
    return (
      <SortableTable
        {...this.props}
        columns={[checkboxColumn, ...this.props.columns]}
        rows={this.getRowsWithCheckboxes()}
        emptyTableText={this.props.emptyTableText}
        dataTestAttrs={{component: 'CheckableSortableTable', element: 'root'}}
      />
    );
  }
}

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default CheckableSortableTable;
